import React, { useEffect, useState } from 'react';
import { CalendarPlus, Copy, RotateCcw } from 'lucide-react';
import { getPlannerFeedToken, createPlannerFeedToken, resetPlannerFeedToken } from '../../api';

// "Add my schedule to my calendar" (Club Planner, Phase 6) — used at the top of the player's
// Schedule page and in the staff week view.
//
// One tap: creates the personal feed link if there is none yet, then hands it to the device's
// calendar — iPhone / iPad / Mac open the Calendar app's subscribe screen (webcal://), Android
// opens Google Calendar's "add calendar from URL" screen (Android has no webcal handler). The
// calendar then refreshes by itself. "Reset link" revokes the old link (anyone who had it gets
// nothing anymore) and makes a new one, which then has to be added again.

const btn = {
  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, minHeight: 44, borderRadius: 10,
  border: '1px solid var(--border-strong)', background: 'var(--bg-card)', color: 'var(--text-primary)',
  fontWeight: 600, fontSize: 14, cursor: 'pointer', fontFamily: 'inherit', textDecoration: 'none', padding: '0 14px',
};

function platform() {
  const ua = navigator.userAgent || '';
  if (/Android/i.test(ua)) return 'android';
  if (/iPhone|iPad|iPod|Macintosh/i.test(ua)) return 'apple';
  return 'other';
}

// Where one tap should take the device.
function calendarTarget(feed) {
  if (platform() === 'android') return `https://calendar.google.com/calendar/r?cid=${encodeURIComponent(feed.webcal_url)}`;
  return feed.webcal_url;
}

export function useCalendarFeed(token) {
  const [feed, setFeed] = useState(undefined); // undefined = loading, null = none yet
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    if (!token) return;
    getPlannerFeedToken(token).then(setFeed).catch(() => setFeed(null));
  }, [token]);

  // Create-if-needed, then go: a location change (not a popup), so it isn't blocked after the await.
  const addToCalendar = async () => {
    setBusy(true);
    try {
      const f = feed || await createPlannerFeedToken(token);
      setFeed(f);
      setMsg(platform() === 'other' ? 'If nothing opened, copy the link and add it in your calendar app as a subscription.' : null);
      window.location.href = calendarTarget(f);
    } catch (e) { setMsg(e.message); } finally { setBusy(false); }
  };
  const reset = async () => {
    if (!window.confirm('Make a new link? The old one stops working, so you have to add the calendar again on every device that uses it.')) return;
    setBusy(true);
    try { setFeed(await resetPlannerFeedToken(token)); setMsg('New link made. Tap "Add to my calendar" again on your devices.'); } catch (e) { setMsg(e.message); } finally { setBusy(false); }
  };
  const copy = async () => {
    try {
      const f = feed || await createPlannerFeedToken(token);
      setFeed(f);
      await navigator.clipboard.writeText(f.url);
      setMsg('Link copied.');
    } catch { setMsg(feed?.url || 'Could not copy the link.'); }
  };
  return { feed, busy, msg, addToCalendar, reset, copy };
}

export default function CalendarFeedLink({ token, audienceText }) {
  const { feed, busy, msg, addToCalendar, reset, copy } = useCalendarFeed(token);
  if (feed === undefined) return null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <button type="button" style={btn} disabled={busy} onClick={addToCalendar}>
        <CalendarPlus size={16} /> {busy ? 'Opening your calendar…' : 'Add my schedule to my calendar'}
      </button>
      <span style={{ fontSize: 11, color: 'var(--text-muted)', textAlign: 'center' }}>
        Personal feed · {audienceText} · updates automatically
      </span>
      <details style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
        <summary style={{ cursor: 'pointer', textAlign: 'center' }}>More options</summary>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}>
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" style={{ ...btn, flex: 1, fontSize: 13 }} disabled={busy} onClick={copy}><Copy size={14} /> Copy link</button>
            {feed && <button type="button" style={{ ...btn, flex: 1, fontSize: 13 }} disabled={busy} onClick={reset}><RotateCcw size={14} /> Reset link</button>}
          </div>
          <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
            Other calendar apps (e.g. Outlook): copy the link and add it as a calendar subscription.
            Reset makes a new link and stops the old one, e.g. if you shared it by mistake.
          </span>
        </div>
      </details>
      {msg && <span role="status" style={{ fontSize: 12, color: 'var(--text-secondary)', textAlign: 'center', wordBreak: 'break-all' }}>{msg}</span>}
    </div>
  );
}
